/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Intercom, { show, shutdown, update } from '@intercom/messenger-js-sdk';
import { connect } from 'react-redux';
import request from '../../utils/request';

const INTERCOM_APP_ID = 'pqvvfc6t';

export const IntercomContext = createContext({});

const IntercomContextProvider = ({ children, auth }) => {
  const isBootedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  // Initialize Intercom when user is authenticated
  useEffect(() => {
    const initIntercom = async () => {
      if (auth.userId && auth.email && !isBootedRef.current) {
        try {
          // Fetch the identity verification hash from backend
          const response = await request.get('/intercom/user-hash');
          const { userHash } = response.data;

          const userName = `${auth.firstName || ''} ${auth.lastName || ''}`.trim() || 'User';
          Intercom({
            app_id: INTERCOM_APP_ID,
            user_id: String(auth.userId),
            user_hash: userHash,
            name: userName,
            email: auth.email,
            company: auth.brandName
              ? { company_id: String(auth.merchantId), name: auth.brandName }
              : undefined,
            hide_default_launcher: true,
            page_url: window.location.href,
            // Custom attributes to bypass Zendesk sync restrictions
            cb_name: userName,
            cb_email: auth.email,
            cb_company: auth.brandName || '',
            cb_page_url: window.location.href,
          });
          isBootedRef.current = true;
          setIsReady(true);
        } catch (error) {
          // Fall back to initializing without identity verification
          console.error('Failed to fetch Intercom user hash:', error);
          const userName = `${auth.firstName || ''} ${auth.lastName || ''}`.trim() || 'User';
          Intercom({
            app_id: INTERCOM_APP_ID,
            user_id: String(auth.userId),
            name: userName,
            email: auth.email,
            company: auth.brandName
              ? { company_id: String(auth.merchantId), name: auth.brandName }
              : undefined,
            hide_default_launcher: true,
            page_url: window.location.href,
            // Custom attributes to bypass Zendesk sync restrictions
            cb_name: userName,
            cb_email: auth.email,
            cb_company: auth.brandName || '',
            cb_page_url: window.location.href,
          });
          isBootedRef.current = true;
          setIsReady(true);
        }
      }
    };

    initIntercom();
  }, [auth.userId, auth.email, auth.firstName, auth.lastName, auth.brandName, auth.merchantId]);

  // Handle logout - shutdown Intercom when user logs out
  useEffect(() => {
    if (!auth.userId && isBootedRef.current) {
      shutdown();
      isBootedRef.current = false;
      setIsReady(false);
    }
  }, [auth.userId]);

  // Open chat programmatically with current page context
  const openChat = useCallback(() => {
    if (isBootedRef.current) {
      // Update with current page before opening so Fin AI knows where the user is
      update({
        last_request_at: Math.floor(Date.now() / 1000),
        page_url: window.location.href,
        cb_page_url: window.location.href,
      });
      show();
    }
  }, []);

  return <IntercomContext.Provider value={{ openChat, isReady }}>{children}</IntercomContext.Provider>;
};

export function useIntercom() {
  const context = useContext(IntercomContext);

  if (!context) {
    throw new Error('useIntercom must be used within an IntercomContextProvider');
  }
  return context;
}

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(IntercomContextProvider);
